import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Heart, LogOut, ShieldCheck, UserRound, Settings, CircleHelp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { openSupportEmail, supportEmailHref } from "@/lib/supportEmail";

export default function ParticipantProfileMenu({ links = null }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const defaultLinks = user?.account_type === "traveller" ? [
    { to: "/traveller", label: "Travel hub", icon: BadgeCheck },
    { to: "/traveller/trips", label: "My trips", icon: ArrowLeft },
    { to: "/traveller/wishlist", label: "Wishlist", icon: Heart },
    { to: "/traveller/profile", label: "Profile & privacy", icon: UserRound },
  ] : [
    { to: "/my/hub", label: "Travel hub", icon: BadgeCheck },
    { to: "/my", label: "My trips", icon: ArrowLeft },
    { to: "/my/privacy", label: "Privacy", icon: ShieldCheck },
  ];
  const accountName = user?.full_name || user?.email || "Participant";
  const hasDistinctEmail = Boolean(
    user?.email &&
    user?.full_name &&
    user.full_name.trim().toLowerCase() !== user.email.trim().toLowerCase()
  );
  const currentPath = location.pathname.replace(/\/$/, "") || "/";
  const menuLinks = [...(links ?? defaultLinks), { to: "/account/settings", label: "Settings", icon: Settings }].filter((item) => {
    const itemPath = item.to.replace(/\/$/, "") || "/";
    return itemPath !== currentPath;
  });

  return (
    <div className="participant-profile-menu" ref={ref}>
      <button
        type="button"
        data-testid="participant-profile-menu-button"
        aria-label="Participant profile menu"
        aria-expanded={open}
        aria-haspopup="menu"
        className="participant-profile-menu-button"
        onClick={() => setOpen((value) => !value)}
      >
        <UserRound className="h-5 w-5" />
      </button>
      {open && (
        <div className="participant-profile-menu-panel" role="menu">
          <div className="participant-profile-menu-identity">
            <span><UserRound className="h-4 w-4" /> Signed in</span>
            {hasDistinctEmail && <small>{user.email}</small>}
          </div>
          <div className="participant-profile-menu-items">
            {menuLinks.map((item) => {
              const Icon = item.icon || BadgeCheck;
              return (
                <Link
                  key={`${item.to}-${item.label}`}
                  to={item.to}
                  role="menuitem"
                  className="participant-profile-menu-item"
                  onClick={() => setOpen(false)}
                >
                  <Icon className="h-4 w-4" /> {item.label}
                </Link>
              );
            })}
            <a href={supportEmailHref(user)} target="_blank" rel="noopener noreferrer" role="menuitem" className="participant-profile-menu-item" onClick={(event) => { openSupportEmail(event, user); setOpen(false); }}><CircleHelp className="h-4 w-4" /> Help &amp; support</a>
            <button
              type="button"
              role="menuitem"
              data-testid="logout-btn"
              className="participant-profile-menu-item participant-profile-menu-signout"
              onClick={async () => {
                setOpen(false);
                await logout();
                nav("/login");
              }}
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
